import { useEffect, useMemo, useState } from "react";
import { useParams } from "react-router-dom";
import { Page, PageStatus } from "src/components";
import { Button, Color, Flex, Search, Text, SubgridTable, Link } from "src/elements";
import { Icon } from "src/elements";
import { useMenu } from "src/components/Menu";
import { useApi } from "src/api";
import { useLoaders } from "src/components/InlineLoader";
import { RouteGetCategories_categoryId_StoreFixtures } from "@CommonApi/categories/_categoryId_/store-fixtures";
import { ResponseGetCategories_categoryId_StoreFixtures } from "@CommonApi/categories/_categoryId_/store-fixtures";
import { useAsyncState } from "src/hooks/useAsyncState";
import { pages } from "src/utils";
import { routeToUrl } from "@CommonUtils/routes";

export const PageStoreFixtures = () => {
  const { categoryId: _categoryId } = useParams<{ categoryId: string }>();
  const categoryId = Number(_categoryId);
  const { getCategories_categoryId_StoreFixtures, getCategories_categoryId_Api } = useApi();
  const { hasLoader } = useLoaders();

  const [storeFixtures, setStoreFixtures] =
    useAsyncState<ResponseGetCategories_categoryId_StoreFixtures[]>();
  const [categoryName, setCategoryName] = useAsyncState<string>();
  const [search, setSearch] = useState("");
  const [selectedBaySize, setSelectedBaySize] = useState<string>("ALL");
  const { openMenu } = useMenu();

  const isStoreFixturesLoading = hasLoader(RouteGetCategories_categoryId_StoreFixtures);

  // Get unique bay sizes for filter
  const baySizes = useMemo(() => {
    const sizes = (storeFixtures || []).map((fixture) => fixture.baySize);
    return [...new Set(sizes)].sort((a, b) => a - b);
  }, [storeFixtures]);

  // Filter store fixtures based on search and bay size
  const filteredStoreFixtures = useMemo(() => {
    let filtered = storeFixtures || [];

    if (search) {
      const searchLowercase = search.toLowerCase();
      filtered = filtered.filter((fixture) =>
        fixture.storeCode.toLowerCase().includes(searchLowercase),
      );
    }

    if (selectedBaySize !== "ALL") {
      filtered = filtered.filter((fixture) => fixture.baySize === parseInt(selectedBaySize));
    }

    return filtered;
  }, [storeFixtures, search, selectedBaySize]);

  useEffect(() => {
    if (categoryId) {
      loadStoreFixtures();
      loadCategoryName();
    }
  }, [categoryId]);

  const loadStoreFixtures = async () => {
    const response = await getCategories_categoryId_StoreFixtures(categoryId);
    setStoreFixtures(response);
  };

  const loadCategoryName = async () => {
    const category = await getCategories_categoryId_Api({ categoryId: categoryId.toString() });
    if (category) {
      setCategoryName(category.categoryCode);
    }
  };

  const renderInitialFixturesIcon = (fixture: ResponseGetCategories_categoryId_StoreFixtures) => {
    // Create a simple bar chart representation
    const barHeights = [24, 36, 30, 18]; // Different heights for visual variety

    return (
      <Link
        to={routeToUrl(pages.categories_categoryId_stores_storeId_Fixtures, {
          categoryId: categoryId.toString(),
          storeId: fixture.storeId.toString(),
        })}
      >
        <Flex align="center" justify="center" gap="3px" height="40px">
          {barHeights.map((height, index) => (
            <Flex
              key={index}
              width="10px"
              height={`${height}px`}
              background={Color.primary}
              borderRadius="3px"
            />
          ))}
        </Flex>
      </Link>
    );
  };

  return (
    <Page>
      <Flex align="center" gap="6px">
        <Text variant="h3">Store Fixtures - </Text>
        <Text
          variant="h3"
          isLoading={isStoreFixturesLoading}
          isLoadingWidth="100px"
          textTransform="uppercase"
        >
          {categoryName}
        </Text>
      </Flex>

      <Flex align="center" gap="16px">
        <Search
          width="200px"
          search={search}
          setSearch={setSearch}
          placeholder="Search stores..."
        />
      </Flex>

      {isStoreFixturesLoading ? (
        <PageStatus icon="spinner" label="Loading Store Fixtures..." />
      ) : filteredStoreFixtures.length === 0 ? (
        <PageStatus icon="info" color={Color.spaceGray} label="No store fixtures found" />
      ) : (
        <SubgridTable
          columns={[
            { id: "storeCode", header: "Store code", justify: "left" },
            {
              id: "fixtures",
              header: "Initial Fixtures",
              renderer: ({ row }) => renderInitialFixturesIcon(row),
              justify: "center",
            },
            {
              id: "baySize",
              header: "Bay size",
              headerRenderer: (
                <Button
                  variant="borderless"
                  onClick={(event) =>
                    openMenu({
                      event: event,
                      menu: {
                        title: "Bay size",
                        options: [
                          { label: "All", onClick: () => setSelectedBaySize("ALL") },
                          ...baySizes.map((size) => ({
                            label: size.toString(),
                            onClick: () => setSelectedBaySize(size.toString()),
                          })),
                        ],
                      },
                      isRelativeToElement: true,
                    })
                  }
                >
                  <Flex align="center" gap="6px">
                    <Text variant="small1" color={Color.white} bold>
                      Bay size
                    </Text>
                    <Icon name="triangleDown" color={Color.white} size={10} />
                  </Flex>
                </Button>
              ),
              justify: "left",
            },
          ]}
          data={filteredStoreFixtures}
        />
      )}
    </Page>
  );
};
